import { forwardRef, ReactNode } from "react";

import Box from "@mui/material/Box";
import { SxProps, Theme } from "@mui/material/styles";
import {
  AvailablePaymentCurrency,
  formatCurrencyAmount,
  formatDateOnly,
  formatDateTimeToFormat,
  formatDateToMedDate,
  getDollarValue,
  IsoLocale,
  Locales,
} from "@simplyk/common";

import { TableIconTextCell, TableTwoLineCell } from "../TableCompositeCells";
import { TableCheckboxCell, TableChipListCell, TableSingleSelectCell } from "../TableSelectCells";
import {
  TableDateCell,
  TableDatetimeCell,
  TableEmphasisCell,
  TableMonetaryCell,
  TableNumberCell,
  TableTextCell,
} from "../TableTextCells";
import { TableCellChip, TableCellType, tableCellTypeOptions } from "../types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { TableCopyableCell } from "@/dashboard-design-system/DashboardTableCustomization/TableCopyableCell/TableCopyableCell";
import { TableEmailCell } from "@/dashboard-design-system/DashboardTableCustomization/TableEmailCell/TableEmailCell";
import { TableOverflowMultiSelectCell } from "@/dashboard-design-system/DashboardTableCustomization/TableOverflowMultiSelectCell/TableOverflowMultiSelectCell";
import { IconElement } from "@/icons/types";

export type CellValueType = Exclude<TableCellType, "quickAction"> | "custom";

export interface CellProps {
  valueType: CellValueType;
  value?: unknown;
  locale: Locales;
  isoLocale: IsoLocale;
  currency: AvailablePaymentCurrency;
  labels: { yes: string; no: string; copy: string };
  formatLabel?: (value: string) => string;
  dateOnly?: boolean;
  dateFormat?: string;
  datetimeDisplay?: "date" | "datetime";
  deduplicate?: boolean;
  secondary?: ReactNode;
  icon?: IconElement;
  chips?: TableCellChip[];
  children?: ReactNode;
  sensitiveData?: string;
  emailAction?: { label: string; onSendEmail: (email: string) => void };
  sx?: SxProps<Theme>;
}

export const Cell = forwardRef<HTMLDivElement, CellProps>(function Cell(props, ref) {
  const content = renderCellValue(props);
  if (content === null || content === undefined) {
    return null;
  }
  return (
    <Box ref={ref} data-sensitive={props.sensitiveData} sx={mergeSx({ minWidth: 0 }, props.sx)}>
      {content}
    </Box>
  );
});

export const getCellValueType = (valueType: string | undefined, value: unknown): CellValueType => {
  if (Array.isArray(value)) {
    return "multiSelect";
  }
  if (typeof value === "boolean") {
    return "checkbox";
  }
  return (
    tableCellTypeOptions.find(
      (type): type is Exclude<TableCellType, "quickAction"> => type !== "quickAction" && type === valueType
    ) ?? "text"
  );
};

export const formatCellDate = (
  value: string | number,
  locale: Locales,
  dateOnly = false,
  dateFormat?: string
): string =>
  isDateOnlyValue(value, dateOnly)
    ? formatDateOnly(value, locale, dateFormat ?? { year: "numeric", month: "short", day: "numeric" })
    : dateFormat
      ? formatDateTimeToFormat(value, locale, dateFormat)
      : formatDateToMedDate(new Date(value), locale);

const renderCellValue = ({
  valueType,
  value,
  locale,
  isoLocale,
  currency,
  labels,
  formatLabel = String,
  dateOnly = false,
  dateFormat,
  datetimeDisplay = "datetime",
  deduplicate = false,
  secondary,
  icon,
  chips,
  children,
  emailAction,
}: CellProps): ReactNode => {
  if (valueType === "custom") {
    return children;
  }
  if (valueType === "chipList") {
    return chips?.length ? <TableChipListCell chips={chips} /> : null;
  }
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
    return null;
  }
  const text = String(value);
  switch (valueType) {
    case "emphasis":
    case "monetary": {
      const formattedAmount = formatCurrencyAmount({
        value: getDollarValue(Number(value)),
        locale: isoLocale,
        currency,
      });
      return valueType === "emphasis" ? (
        <TableEmphasisCell value={formattedAmount} />
      ) : (
        <TableMonetaryCell value={formattedAmount} />
      );
    }
    case "number":
      return <TableNumberCell value={text} />;
    case "date":
    case "datetime": {
      const date = value instanceof Date ? value.getTime() : typeof value === "number" ? value : text;
      const formattedDate = formatCellDate(date, locale, dateOnly, dateFormat);
      const fullDatetime = isDateOnlyValue(date, dateOnly) ? formattedDate : new Date(date).toLocaleString(isoLocale);
      return valueType === "datetime" && datetimeDisplay === "datetime" ? (
        <TableDatetimeCell value={fullDatetime} />
      ) : (
        <TableDateCell value={formattedDate} fullDatetime={fullDatetime} />
      );
    }
    case "checkbox":
      return <TableCheckboxCell isChecked={value === true} yesLabel={labels.yes} noLabel={labels.no} />;
    case "email":
      if (emailAction) {
        return <TableEmailCell value={text} sendEmailLabel={emailAction.label} onSendEmail={emailAction.onSendEmail} />;
      }
      return <TableCopyableCell key={`email:${text}`} value={text} valueType="email" copyLabel={labels.copy} />;
    case "phoneNumber":
    case "url":
      return (
        <TableCopyableCell key={`${valueType}:${text}`} value={text} valueType={valueType} copyLabel={labels.copy} />
      );
    case "singleSelect":
      return <TableSingleSelectCell label={formatLabel(text)} />;
    case "multiSelect": {
      const values: unknown[] = Array.isArray(value) ? value : text.split(", ");
      const formattedLabels = values.map((item) =>
        formatLabel(item === null || item === undefined ? "" : String(item))
      );
      return <TableOverflowMultiSelectCell labels={deduplicate ? [...new Set(formattedLabels)] : formattedLabels} />;
    }
    case "twoLine":
      return <TableTwoLineCell primary={text} secondary={secondary} />;
    case "iconText":
      return <TableIconTextCell value={text} icon={icon} />;
    default:
      return <TableTextCell value={formatLabel(text)} />;
  }
};

const isDateOnlyValue = (value: string | number, dateOnly: boolean) =>
  dateOnly || (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value));
